import { clientReducerActions } from "../../../_services/store";
import * as clientService from "./clients.service";
import {
    getClientByExternalUUIDFromClients,
    getModulesToActivateForConnection,
    mapConnectedAuthDataPropsToDTO,
} from "./client.mapper";
import { AuthDataIdentifier } from "../../../_statics/AuthDataType";
import { Modes } from "../../../_statics/CRUDStatics";
import stringUtils from "../../../_services/string-utils";
import { organizationService } from "../../myOrganization/services/organization.service";
import { reloadUserData } from "../../../user/services/store/authentication.actions";

export const getClientsForSelectedOrganization = () => {
    return async (dispatch) => {
        dispatch(clientReducerActions.getClientsForSelectedOrganizationRequest());
        try {
            const clients = await clientService.getClientsForSelectedOrganization();
            dispatch(clientReducerActions.getClientsForSelectedOrganizationSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.getClientsForSelectedOrganizationFailure(error));
        }
    };
};

// Same fetch without the loading flag and without clearing the error messages, so a background poll (the
// setup card waiting for a connection to be confirmed) doesn't blank out the screen that triggered it
export const refreshClientsQuietly = () => {
    return async (dispatch) => {
        try {
            const clients = await clientService.getClientsForSelectedOrganization();
            dispatch(clientReducerActions.getClientsForSelectedOrganizationSuccess(clients));
            return true;
        } catch (error) {
            // Background refresh — keeps whatever is on screen, but reports the failure so a poll that
            // keeps failing can give up instead of waiting out its deadline in silence
            return false;
        }
    };
};

export const getClientsForParallelTestOrganization = (organizationUUID) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.getClientsForParallelTestOrganizationRequest());
        try {
            const clients = await clientService.getClientsForParallelTestOrganization(organizationUUID);
            dispatch(clientReducerActions.getClientsForParallelTestOrganizationSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.getClientsForParallelTestOrganizationFailure(error));
        }
    };
};

export const createClientForSelectedOrganization = (client) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.createClientRequest());
        try {
            const clients = await clientService.createClient(client);
            dispatch(clientReducerActions.createClientSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.createClientFailure(error));
        }
    };
};

export const updateClient = (client) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.updateClientRequest());
        try {
            const clients = await clientService.updateClient(client);
            dispatch(clientReducerActions.updateClientSuccess(clients));
            return getClientByExternalUUIDFromClients(clients, client.externalUUID);
        } catch (error) {
            dispatch(clientReducerActions.updateClientFailure(error));
        }
    };
};

export const updateClientDatevSettings = (clientData) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.updateClientRequest());
        try {
            const clients = await clientService.updateClientDatevSettings(clientData);
            dispatch(clientReducerActions.updateClientDatevSettingsSuccess(clients));
            return clients;
        } catch (error) {
            dispatch(clientReducerActions.updateClientFailure(error));
            throw error;
        }
    };
};

export const deleteClient = (client) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.deleteClientRequest());
        try {
            const clients = await clientService.deleteClient(client.externalUUID);
            dispatch(clientReducerActions.deleteClientSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.deleteClientFailure(error));
        }
    };
};

export const createConnectedAuthDataForClient = (connectedAuthData) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.createConnectedAuthDataForClientRequest());
        try {
            const clients = await clientService.createConnectedAuthDataForClient(connectedAuthData);
            dispatch(clientReducerActions.createConnectedAuthDataForClientSuccess(clients));
            dispatch(activateModuleForConnection(connectedAuthData.type));
        } catch (error) {
            dispatch(clientReducerActions.createConnectedAuthDataForClientFailure(error));
        }
    };
};

// Auto-activates the module(s) behind a freshly created credential-based connection so that the
// downstream import/config screens (guarded by allowedApps) are immediately usable (issue #41843).
// OAuth-based connections already auto-activate their module on the backend, so those are skipped.
export const activateModuleForConnection = (type) => {
    return async (dispatch, getState) => {
        const selectedOrganization = getState().authentication.user?.selectedOrganization;
        if (!selectedOrganization) {
            return;
        }
        const modulesToActivate = getModulesToActivateForConnection(type, selectedOrganization.allowedApps);
        if (modulesToActivate.length === 0) {
            return;
        }
        try {
            const updatedAllowedApps = [...(selectedOrganization.allowedApps || []), ...modulesToActivate];
            await organizationService.updateAllowedApps(selectedOrganization.externalUUID, updatedAllowedApps);
            dispatch(reloadUserData());
        } catch (error) {
            // Non-fatal: the connection was created successfully; the module can still be activated
            // manually under "Weitere Integrationen" if the auto-activation call fails.
            console.error("Auto-activation of module for connection failed:", type, error);
        }
    };
};

export const updateConnectedAuthDataForClient = (connectedAuthData) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.updateConnectedAuthDataForClientRequest());
        try {
            const clients = await clientService.updateConnectedAuthDataForClient(connectedAuthData);
            dispatch(clientReducerActions.updateConnectedAuthDataForClientSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.updateConnectedAuthDataForClientFailure(error));
        }
    };
};

export const deleteConnectedAuthDataForClient = (connectedAuthData) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.deleteConnectedAuthDataForClientRequest());
        try {
            const clients = await clientService.deleteConnectedAuthDataForClient(connectedAuthData);
            dispatch(clientReducerActions.deleteConnectedAuthDataForClientSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.deleteConnectedAuthDataForClientFailure(error));
        }
    };
};

export const prepareAuthDataOnboarding = (authDataType, request) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.prepareAuthDataOnboardingRequest());
        try {
            let prepareOnboardingResponse;
            if (authDataType === AuthDataIdentifier.PAYPAL) {
                prepareOnboardingResponse = await clientService.preparePaypalConnectOnboarding(request);
            }

            dispatch(
                clientReducerActions.prepareAuthDataOnboardingSuccess(
                    prepareOnboardingResponse.authLink ? prepareOnboardingResponse.authLink : prepareOnboardingResponse
                )
            );

            return prepareOnboardingResponse;
        } catch (error) {
            dispatch(clientReducerActions.prepareAuthDataOnboardingFailure(error));
            throw error;
        }
    };
};

export const getDatevConnections = () => {
    return async (dispatch) => {
        dispatch(clientReducerActions.getDatevConnectionsRequest());
        try {
            const response = await clientService.getDatevConnections();
            dispatch(clientReducerActions.getDatevConnectionsSuccess(response));
        } catch (error) {
            dispatch(clientReducerActions.getDatevConnectionsFailure(error));
        }
    };
};

export const prepareDatevUOOnboarding = (request) => {
    return async (dispatch) => {
        dispatch(
            clientReducerActions.prepareDatevUOOnboardingRequest(request.consultantNumber + "-" + request.clientNumber)
        );
        try {
            const prepareOnboardingResponse = await clientService.prepareDatevUOOnboarding(request);
            dispatch(
                clientReducerActions.prepareDatevUOOnboardingSuccess(
                    prepareOnboardingResponse.authLink ? prepareOnboardingResponse.authLink : prepareOnboardingResponse
                )
            );

            return prepareOnboardingResponse;
        } catch (error) {
            dispatch(clientReducerActions.prepareDatevUOOnboardingFailure(error));
            throw error;
        }
    };
};

export const removeDatevConnection = (request) => {
    return async (dispatch) => {
        dispatch(
            clientReducerActions.removeDatevConnectionRequest(request.consultantNumber + "-" + request.clientNumber)
        );
        try {
            const datevId = await clientService.removeDatevConnection(request);
            dispatch(clientReducerActions.removeDatevConnectionSuccess(datevId));
        } catch (error) {
            dispatch(clientReducerActions.removeDatevConnectionFailure(error));
        }
    };
};

export const addConnectedAuthDataId = (clientUUID, authDataId) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.connectAuthDataWithClientRequest());
        try {
            await clientService.addConnectedAuthDataId({
                connectedAuthDataExternalUUID: authDataId,
                selectedPlatformClientExternalUUID: clientUUID,
            });
            dispatch(clientReducerActions.connectAuthDataWithClientSuccess());
        } catch (error) {
            dispatch(clientReducerActions.connectAuthDataWithClientFailure(error));
        }
    };
};

export const createNewConnectedAuthDataForClient = (client, initialConnectedAuthData) => {
    return (dispatch) => {
        dispatch(clientReducerActions.resetErrorMessages());
        dispatch(clientReducerActions.setEditConnectedAuthDataMode(Modes.CREATE));
        const preparedConnectedAuthData = { ...initialConnectedAuthData };
        preparedConnectedAuthData.platformClientExternalUUID = client.externalUUID;
        dispatch(clientReducerActions.setEditConnectedAuthData(preparedConnectedAuthData));
        dispatch(clientReducerActions.toggleEditConnectedAuthDataModalForClient());
    };
};

export const createNewConnectedAuthDataForClientFromRedirect = (client, initialConnectedAuthData) => {
    // Perform create AuthData from redirect - without opening the create-connection-modal.
    return (dispatch) => {
        dispatch(clientReducerActions.resetErrorMessages());
        dispatch(clientReducerActions.setEditConnectedAuthDataMode(Modes.CREATE));
        const preparedConnectedAuthData = { ...initialConnectedAuthData };
        preparedConnectedAuthData.platformClientExternalUUID = client.externalUUID;
        dispatch(clientReducerActions.setEditConnectedAuthData(preparedConnectedAuthData));
    };
};

export const getRedirectingLinkBySelectedAuthDataType = (connectedAuthDataType, clientUUID) => {
    return (dispatch) => {
        if (connectedAuthDataType === AuthDataIdentifier.AMAZON) {
            return (
                "https://sellercentral.amazon.com/apps/authorize/consent?application_id=" +
                process.env.REACT_APP_AMAZON_APPLICATION_ID +
                "&state=" +
                clientUUID +
                "&version=beta" +
                "&redirect_uri=" +
                process.env.REACT_APP_API_BASE_URL +
                "/oauth/amazon/connect"
            );
        } else if (connectedAuthDataType === AuthDataIdentifier.MOLLIE) {
            return (
                "https://www.mollie.com/oauth2/authorize" +
                "?response_type=code" +
                "&approval_prompt=force" +
                "&client_id=" +
                process.env.REACT_APP_MOLLIE_CLIENT_ID +
                "&state=" +
                clientUUID +
                "&redirect_uri=" +
                process.env.REACT_APP_API_BASE_URL +
                "/oauth/mollie/connect" +
                "&scope=payments.read refunds.read customers.read profiles.read invoices.read settlements.read orders.read balances.read balance-reports.read organizations.read onboarding.read"
            );
        } else if (connectedAuthDataType === AuthDataIdentifier.OTTO_OAUTH) {
            return process.env.REACT_APP_OTTO_INSTALLATION_LINK + "?state=" + clientUUID;
        } else if (connectedAuthDataType === AuthDataIdentifier.EBAY_OAUTH) {
            clientService.getEbayOAuthState(clientUUID).then((response) => {
                const stateToken = response.data.state;
                const authLink = process.env.REACT_APP_EBAY_AUTHORIZATION_LINK +
                    "&state=" + encodeURIComponent(stateToken);
                dispatch(clientReducerActions.setAuthLink(authLink));
            }).catch(() => {
                // Fallback: use clientUUID directly if state endpoint fails
                const authLink = process.env.REACT_APP_EBAY_AUTHORIZATION_LINK +
                    "&state=" + clientUUID;
                dispatch(clientReducerActions.setAuthLink(authLink));
            });
            return null; // async — link will be set via dispatch once state token is fetched
        } else if (connectedAuthDataType === AuthDataIdentifier.PAYPAL) {
            dispatch(
                prepareAuthDataOnboarding(connectedAuthDataType, {
                    externalUUID: clientUUID,
                })
            );
            return null; // Paypal needs some time to preprocess. Clear the auth link until a successful response will re-set it
        } else if (connectedAuthDataType === AuthDataIdentifier.STRIPE_CONNECT) {
            return (
                "https://connect.stripe.com/oauth/authorize" +
                "?response_type=code" +
                "&client_id=" +
                process.env.REACT_APP_STRIPE_CLIENT_ID +
                "&redirect_uri=" +
                process.env.REACT_APP_API_BASE_URL +
                "/oauth/stripe/connect" +
                "&state=" +
                clientUUID
            );
        } else if (connectedAuthDataType === AuthDataIdentifier.SHOPIFY_APPSTORE_BILLING) {
            return "https://apps.shopify.com/pathway-rechnungsprinter?locale=de&surface_intra_position=2&surface_type=partners&surface_version=redesign";
        } else if (connectedAuthDataType === AuthDataIdentifier.SHOPIFY_APPSTORE_PLATFORM) {
            return "https://apps.shopify.com/pathway-solutions?locale=de&surface_intra_position=1&surface_type=partners&surface_version=redesign";
        } else if (connectedAuthDataType === AuthDataIdentifier.TIKTOK_OAUTH) {
            return process.env.REACT_APP_TIK_TOK_AUTHORIZATION_LINK + "&state=" + clientUUID;
        }
    };
};

export const searchClientsFromProd = (request) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.searchClientsFromProdRequest());
        try {
            const prodClients = await clientService.searchClientsFromProd(request);
            dispatch(clientReducerActions.searchClientsFromProdSuccess(prodClients));
        } catch (error) {
            dispatch(clientReducerActions.searchClientsFromProdFailure(error));
        }
    };
};

export const autoUpdateClientFromProd = (request) => {
    return async (dispatch) => {
        dispatch(clientReducerActions.autoUpdateClientFromProdRequest());
        try {
            const clients = await clientService.autoUpdateClientFromProd(request);
            dispatch(clientReducerActions.autoUpdateClientFromProdSuccess(clients));
        } catch (error) {
            dispatch(clientReducerActions.autoUpdateClientFromProdFailure(error));
        }
    };
};

export const submitEditConnectedAuthData = (clientReducer) => {
    return (dispatch) => {
        const connectedAuthDataToBeUpdated = { ...clientReducer.editConnectedAuthData };
        const updatedAccessData = clientReducer.editAccessData;
        if (clientReducer.editConnectedAuthDataMode === Modes.CREATE) {
            dispatch(createConnectedAuthDataForClient(mapConnectedAuthDataPropsToDTO(connectedAuthDataToBeUpdated)));
        } else {
            if (
                !stringUtils.isBlank(clientReducer.inputLableInformation.endpoint.displayName) &&
                !stringUtils.isBlank(updatedAccessData.endpoint) &&
                !clientReducer.inputLableInformation.endpoint.disabled
            ) {
                connectedAuthDataToBeUpdated.endpoint = updatedAccessData.endpoint;
            }
            if (
                !stringUtils.isBlank(clientReducer.inputLableInformation.key.displayName) &&
                !stringUtils.isBlank(updatedAccessData.key)
            ) {
                connectedAuthDataToBeUpdated.key = updatedAccessData.key;
            }
            if (
                !stringUtils.isBlank(clientReducer.inputLableInformation.value.displayName) &&
                !stringUtils.isBlank(updatedAccessData.value)
            ) {
                connectedAuthDataToBeUpdated.value = updatedAccessData.value;
            }
            if (
                !stringUtils.isBlank(clientReducer.inputLableInformation.token.displayName) &&
                !stringUtils.isBlank(updatedAccessData.token)
            ) {
                connectedAuthDataToBeUpdated.token = updatedAccessData.token;
            }
            if (
                !stringUtils.isBlank(clientReducer.inputLableInformation.sharedSecret.displayName) &&
                !stringUtils.isBlank(updatedAccessData.sharedSecret)
            ) {
                connectedAuthDataToBeUpdated.token = updatedAccessData.token;
            }
            dispatch(updateConnectedAuthDataForClient(mapConnectedAuthDataPropsToDTO(connectedAuthDataToBeUpdated)));
        }
    };
};
